import { Component, forwardRef, inject, input, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  FormBuilder,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

type SizeUnit = 'KB' | 'MB' | 'GB';

@Component({
  selector: 'app-size-input',
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SizeInputComponent),
      multi: true,
    },
  ],
  templateUrl: './size-input.component.html',
  styleUrl: './size-input.component.scss',
})
export class SizeInputComponent implements OnInit, ControlValueAccessor {
  private readonly fb = inject(FormBuilder);

  protected readonly form = this.fb.nonNullable.group({
    value: 1,
    unit: 'MB' as SizeUnit,
  });

  private readonly multipliers: Record<SizeUnit, number> = {
    KB: 1024,
    MB: 1024 * 1024,
    GB: 1024 * 1024 * 1024,
  };

  units = input<SizeUnit[]>(['KB', 'MB', 'GB']);

  returnSize = input<SizeUnit>('KB');

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private onChange = (_: number | null) => {
    /* empty */
  };

  private onTouched = () => {
    /* empty */
  };

  ngOnInit(): void {
    this.form.valueChanges.subscribe(({ value, unit }) => {
      if (value && unit)
        this.onChange((value * this.multipliers[unit]) / this.multipliers[this.returnSize()]);
    });
  }

  writeValue(bytes: number | null): void {
    if (bytes == null) {
      this.form.reset(
        {
          value: 1,
          unit: 'MB',
        },
        {
          emitEvent: false,
        },
      );

      return;
    }

    this.form.patchValue(this.fromBytes(bytes), {
      emitEvent: false,
    });
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    if (disabled) this.form.disable();
    else this.form.enable();
  }

  protected touched(): void {
    this.onTouched();
  }

  private fromBytes(bytes: number): { value: number; unit: SizeUnit } {
    if (bytes % this.multipliers.GB === 0) {
      return {
        value: bytes / this.multipliers.GB,
        unit: 'GB',
      };
    }

    if (bytes % this.multipliers.MB === 0) {
      return {
        value: bytes / this.multipliers.MB,
        unit: 'MB',
      };
    }

    return {
      value: bytes / this.multipliers.KB,
      unit: 'KB',
    };
  }
}
